/**
 * The rich half of a composed message, rendered from the plain half.
 *
 * THE SOURCE OF TRUTH IS THE TEXT SOMEBODY TYPED, and this generates HTML from
 * it — the opposite of every WYSIWYG composer, and the choice is deliberate.
 *
 * A `contenteditable` is the obvious way to build a rich composer and it makes
 * the plain-text part a DERIVATION: the browser owns the document, a paste
 * brings arbitrary markup into it, and the `text/plain` half — the one a screen
 * reader, a terminal client and this archive's own index actually read — is
 * whatever an HTML-to-text pass could recover. That is lossy in the direction
 * that matters, and it means the composer has to sanitise pasted HTML, which is
 * the job `apps/web/src/sanitise.ts` records twenty-nine defects from doing.
 *
 * This way round, both halves are exact. The text part is literally what was
 * typed. The HTML part is generated from a grammar with five rules, over text
 * that has ALREADY been escaped — so it cannot contain a tag nobody wrote, and
 * there is no HTML parsing anywhere in the composer at all.
 *
 * WHAT IT COSTS is that the editor is not WYSIWYG: the toolbar inserts markers
 * and a preview shows the result. That is a real loss and it is worth being
 * plain about rather than dressing up. What it buys is that a message can never
 * arrive as something other than what its sender wrote.
 *
 * THE GRAMMAR IS DELIBERATELY TINY. It is the subset that reads correctly as
 * plain text when nothing renders it — which is the property that makes the
 * text part worth sending, and the reason `*bold*` rather than `<b>`.
 */

/** The characters that must not survive into markup, and what they become. */
const ESCAPES: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

/**
 * Escape first, always, and everything.
 *
 * THE ORDER IS THE WHOLE SECURITY ARGUMENT. Every rule below runs over ALREADY
 * ESCAPED text and inserts tags of its own, so the only `<` in the output is one
 * this file wrote. A renderer that escaped afterwards would escape its own tags;
 * one that escaped selectively would have a list of exceptions to get wrong.
 */
export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (character) => ESCAPES[character]!)
}

/**
 * A URL this renderer will put in an `href`.
 *
 * AN ALLOWLIST AND NOT A DENYLIST. `javascript:` is the one everybody knows and
 * `data:`, `vbscript:` and a dozen encodings of each are the ones they forget —
 * so nothing is refused by name. Two schemes are permitted and everything else
 * is rendered as text, which is legible and harmless.
 */
export function safeUrl(raw: string): string | null {
  const trimmed = raw.trim()
  // A scheme cannot contain whitespace or a control character, and a URL that
  // does is one somebody is trying to smuggle something through.
  // NO WHITESPACE, NO CONTROL CHARACTERS, AND NO QUOTES OR ANGLE BRACKETS.
  // The first two cannot appear in a URL at all. The others can only appear
  // percent-encoded, so an unencoded one is a typo or an attempt to end the
  // attribute this is about to be written into.
  //
  // WRITTEN AS ESCAPE SEQUENCES AND NOT AS LITERAL BYTES. The first version of
  // this line carried a real NUL and a real U+001F inside the character class,
  // which made the whole file binary to grep, ripgrep and git diff -- the exact
  // defect apps/web/src/model/keymap.ts already shipped once, where it hid a
  // capability declaration from every search for it.
  if (/[\s\u0000-\u001f\u007f]/.test(trimmed)) return null
  if (/["'<>]/.test(trimmed)) return null
  if (/^https?:\/\/[^\s]+$/i.test(trimmed)) return trimmed
  if (/^mailto:[^\s]+$/i.test(trimmed)) return trimmed
  return null
}

/**
 * Does this message use any of the grammar?
 *
 * DERIVED FROM THE RENDERER RATHER THAN RESTATING ITS PATTERNS, because the
 * first version restated them and they disagreed. `hasMarkup` required
 * whitespace after a closing `*` where `renderMarkup` also accepts punctuation,
 * so `Are you free *Saturday*?` rendered as bold and was reported as having no
 * formatting — the message went out as plain text with the asterisks still in
 * it. Two predicates about one thing is the same defect the paragraph guard had.
 *
 * So: render it, take the scaffolding back off, and see whether anything is
 * left that the plain text did not already say. A message with no formatting
 * renders to nothing but paragraphs and line breaks over its own escaped text.
 */
export function hasMarkup(text: string): boolean {
  if (text.trim() === '') return false
  const rendered = renderMarkup(text)
  const scaffolding = rendered.replace(/<\/?p>/g, '').replace(/<br>/g, '\n')
  const plain = text
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((paragraph) => escapeHtml(paragraph.trim()))
    .filter((paragraph) => paragraph !== '')
    .join('\n')
  return scaffolding.replace(/\n+/g, '\n').trim() !== plain.replace(/\n+/g, '\n').trim()
}

/**
 * Render one message's text as HTML.
 *
 * TOTAL BY CONSTRUCTION. Every input produces output; there is no parse to fail,
 * because the escape pass has already made the text inert and each rule below is
 * a substitution over it. A line this does not recognise comes out as itself.
 */
export function renderMarkup(text: string): string {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  const out: string[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]!

    // A QUOTE. `>` at the start is what every mail client uses and what
    // `quoteBody` writes, so a reply's quoted original renders as one without
    // anybody choosing it.
    if (/^\s*>/.test(line)) {
      const quoted: string[] = []
      while (index < lines.length && /^\s*>/.test(lines[index]!)) {
        quoted.push(lines[index]!.replace(/^\s*>\s?/, ''))
        index++
      }
      // NESTED, by rendering the stripped lines through the whole grammar
      // again. A quoted quote is `>>`, which the strip above turns into `>`.
      out.push(`<blockquote>${renderMarkup(quoted.join('\n'))}</blockquote>`)
      continue
    }

    const bullet = /^\s*[-*]\s+(.*)$/.exec(line)
    const numbered = /^\s*\d+\.\s+(.*)$/.exec(line)
    if (bullet || numbered) {
      const ordered = numbered !== null
      const items: string[] = []
      const pattern = ordered ? /^\s*\d+\.\s+(.*)$/ : /^\s*[-*]\s+(.*)$/
      while (index < lines.length) {
        const match = pattern.exec(lines[index]!)
        if (!match) break
        // AN EMPTY ITEM IS NOT AN ITEM. `- ` with nothing after it is a marker
        // somebody typed and has not filled in yet, and `<li></li>` renders as a
        // stray bullet in every client.
        if (match[1]!.trim() !== '') items.push(`<li>${inline(match[1]!)}</li>`)
        index++
      }
      if (items.length > 0) out.push(`<${ordered ? 'ol' : 'ul'}>${items.join('')}</${ordered ? 'ol' : 'ul'}>`)
      continue
    }

    if (line.trim() === '') {
      index++
      continue
    }

    // A PARAGRAPH IS A RUN OF NON-BLANK LINES, and the line breaks inside it are
    // kept as `<br>`. Joining them into flowing text is what most renderers do
    // and it is wrong for mail: somebody who pressed Enter meant it, and an
    // address block reflowed into one line is the classic symptom.
    //
    // THE GUARD USES THE BLOCK RULES THEMSELVES, and the first version used an
    // approximation of them — `/^\s*[->*]/` — which is wider. A line beginning
    // `*bold*` matched it, started no paragraph, matched no block rule either,
    // and was DROPPED: a message opening with an emphasised word lost its first
    // line silently. Any guard that is not the same predicate as the rules it
    // guards against has that bug somewhere.
    const paragraph: string[] = []
    while (index < lines.length && lines[index]!.trim() !== '' && !startsBlock(lines[index]!)) {
      paragraph.push(inline(lines[index]!))
      index++
    }
    if (paragraph.length > 0) out.push(`<p>${paragraph.join('<br>')}</p>`)
    // A LINE THAT STARTS A BLOCK BUT IS NOT ONE — `- ` with nothing after it —
    // reaches here with an empty paragraph, and advancing is what stops the loop
    // spinning. It renders as nothing, which for a bare marker is right.
    else index++
  }

  return out.join('')
}

/**
 * Does this line begin a block the loop above handles?
 *
 * THE SAME PATTERNS THE BLOCK RULES USE, in one place, so the paragraph guard
 * and the rules it guards against cannot disagree — which is how a line
 * beginning `*bold*` came to be dropped.
 */
function startsBlock(line: string): boolean {
  return /^\s*>/.test(line) || /^\s*[-*]\s+(.*)$/.test(line) || /^\s*\d+\.\s+(.*)$/.test(line)
}

/**
 * The inline rules, over escaped text.
 *
 * ORDER MATTERS AND IS FIXED: links first, so a URL containing an underscore is
 * not italicised halfway through; then emphasis; then bare URLs, which must not
 * re-link what the link rule already wrapped.
 */
function inline(raw: string): string {
  let text = escapeHtml(raw)

  // `[text](url)`. The URL is checked against the allowlist and a refused one is
  // left as the literal text somebody typed — visible, and not a link.
  text = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (whole, label: string, url: string) => {
    const safe = safeUrl(unescapeForUrl(url))
    return safe ? `<a href="${escapeHtml(safe)}">${label}</a>` : whole
  })

  // `*bold*` and `_italic_`, each requiring a non-space just inside the markers
  // so that `2 * 3 * 4` and `snake_case_name` are left alone.
  text = text.replace(/(^|[\s(])\*([^\s*][^*]*?)\*(?=[\s).,!?;:]|$)/g, '$1<strong>$2</strong>')
  text = text.replace(/(^|[\s(])_([^\s_][^_]*?)_(?=[\s).,!?;:]|$)/g, '$1<em>$2</em>')

  // A BARE URL, and only outside an anchor this pass already made. The negative
  // lookbehind on `"` is what keeps it out of an `href` the link rule wrote.
  text = text.replace(/(^|[\s(])(https?:\/\/[^\s<]+[^\s<.,;:!?)])/g, (whole, before: string, url: string) => {
    const safe = safeUrl(unescapeForUrl(url))
    return safe ? `${before}<a href="${escapeHtml(safe)}">${url}</a>` : whole
  })

  return text
}

/**
 * Undo the escaping for the purpose of CHECKING a URL, and only for that.
 *
 * `&amp;` in a query string is `&` to the allowlist's regex, and a URL rejected
 * because its own escaping made it look wrong would be a link that silently
 * stopped working. The value that reaches the `href` is escaped again.
 */
function unescapeForUrl(escaped: string): string {
  return escaped
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
}
